import React, { useRef, useState } from 'react';
import { Map, Flag, TrendingUp, ChevronLeft, ChevronRight, CheckCircle2, Briefcase, Clock } from 'lucide-react';
import { motion } from 'framer-motion';
import RegistrationModal from '../Component/RegistrationModal'; 

// --- Animation Variants ---
const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

// --- Roadmap Data (Preserved exactly as you provided) ---
interface RoadmapStep {
  label: string;
}

interface Roadmap {
  id: number;
  title: string;
  description: string;
  avgPackage: string;
  duration: string;
  steps: RoadmapStep[];
  color: string;
  iconBg: string;
}

const roadmaps: Roadmap[] = [
  {
    id: 1,
    title: 'Java Full Stack Developer',
    description: 'Zero to Enterprise Developer. Best for stability and big MNCs.',
    avgPackage: '5 - 12 LPA',
    duration: '6 Months',
    color: 'border-red-500',
    iconBg: 'bg-red-100 text-red-700',
    steps: [
      { label: 'Core Java & OOPs' },
      { label: 'Advanced Java (JDBC/JSP)' },
      { label: 'Spring Boot & Microservices' },
      { label: 'React JS Frontend' },
      { label: 'MySQL & Deployment' }
    ]
  },
  {
    id: 2,
    title: 'MERN Stack Specialist',
    description: 'Become a modern JS developer. High demand in Startups.',
    avgPackage: '4 - 10 LPA',
    duration: '5 Months',
    color: 'border-blue-500',
    iconBg: 'bg-blue-100 text-blue-700',
    steps: [
      { label: 'HTML5, CSS3 & Tailwind' },
      { label: 'JavaScript (ES6+) & Logic' },
      { label: 'React JS & Redux' },
      { label: 'Node.js & Express' },
      { label: 'MongoDB Database' }
    ]
  },
  {
    id: 3,
    title: 'Data Science & AI Path',
    description: 'Master data analysis and machine learning models.',
    avgPackage: '6 - 15 LPA',
    duration: '7 Months',
    color: 'border-yellow-500',
    iconBg: 'bg-yellow-100 text-yellow-700',
    steps: [
      { label: 'Python Programming' },
      { label: 'Statistics & Math' },
      { label: 'Data Analysis (Pandas)' },
      { label: 'Machine Learning' },
      { label: 'Power BI & Visualization' }
    ]
  },
  {
    id: 4,
    title: 'Non-IT to IT Career Switch',
    description: 'Easiest entry point into the IT industry with no coding fear.',
    avgPackage: '3.5 - 8 LPA',
    duration: '4 Months',
    color: 'border-green-500',
    iconBg: 'bg-green-100 text-green-700',
    steps: [
      { label: 'Computer Fundamentals' },
      { label: 'Manual Testing Basics' },
      { label: 'SQL for Testers' },
      { label: 'Automation (Selenium)' },
      { label: 'API Testing (Postman)' }
    ]
  },
  {
    id: 5,
    title: 'Cloud & DevOps Engineer',
    description: 'Manage servers and cloud infrastructure like a pro.',
    avgPackage: '6 - 14 LPA',
    duration: '6 Months',
    color: 'border-purple-500',
    iconBg: 'bg-purple-100 text-purple-700',
    steps: [
      { label: 'Linux Administration' },
      { label: 'AWS Cloud Basics' },
      { label: 'Docker & Kubernetes' },
      { label: 'CI/CD Pipelines (Jenkins)' },
      { label: 'Terraform & Ansible' }
    ]
  }
];

// --- Roadmap Card Component ---
const RoadmapCard: React.FC<{ roadmap: Roadmap; onRegisterClick: () => void }> = ({ roadmap, onRegisterClick }) => (
  <motion.div
    variants={itemVariants}
    onClick={onRegisterClick}
    className={`relative min-w-[280px] sm:min-w-[320px] w-[280px] sm:w-[320px] flex-shrink-0 bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 border-t-4 ${roadmap.color} flex flex-col justify-between h-[450px] group overflow-hidden mx-2 my-4 cursor-pointer`}
  >
    <div className="p-5 sm:p-6 flex-grow flex flex-col">
      <div className="flex justify-between items-start mb-4">
        <div className={`p-2.5 sm:p-3 rounded-xl ${roadmap.iconBg}`}>
             <Map className="w-5 h-5 sm:w-6 sm:h-6" aria-hidden="true" />
        </div>
        <div className="text-right">
            <span className="block text-[10px] text-gray-400 font-bold uppercase tracking-wider">Duration</span>
            <span className="block text-xs sm:text-sm font-bold text-gray-800 flex items-center justify-end">
                <Clock className="w-3 h-3 mr-1" aria-hidden="true" /> {roadmap.duration}
            </span>
        </div>
      </div>

      <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 leading-tight">
        {roadmap.title}
      </h3>
      <p className="text-gray-500 text-xs sm:text-sm mb-5 leading-relaxed">
        {roadmap.description}
      </p>

      <div className="flex-grow space-y-3 relative">
         <div className="absolute left-2.5 top-2 bottom-2 w-0.5 bg-gray-100"></div>
         {roadmap.steps.map((step, index) => (
             <div key={index} className="relative flex items-center z-10">
                 <div className={`w-5 h-5 rounded-full border-2 border-white shadow-sm flex items-center justify-center mr-3 ${index === roadmap.steps.length - 1 ? 'bg-red-600' : 'bg-green-500'}`}>
                    {index === roadmap.steps.length - 1 ? <Flag className="w-2.5 h-2.5 text-white" /> : <CheckCircle2 className="w-2.5 h-2.5 text-white" />}
                 </div>
                 <span className={`text-[11px] sm:text-xs font-semibold ${index === roadmap.steps.length - 1 ? 'text-red-700' : 'text-gray-700'}`}>
                    {step.label}
                 </span>
             </div>
         ))}
      </div>
    </div>

    <div className="bg-gray-50 px-5 sm:px-6 py-4 border-t border-gray-100 flex justify-between items-center">
        <div>
            <span className="text-[10px] text-gray-400 font-bold uppercase">Avg. Package</span>
            <div className="flex items-center text-gray-900 font-bold text-sm">
                <Briefcase className="w-3.5 h-3.5 mr-1 text-blue-600" />
                {roadmap.avgPackage}
            </div>
        </div>
        <button 
            onClick={(e) => { e.stopPropagation(); onRegisterClick(); }}
            aria-label={`Start journey for ${roadmap.title}`}
            className="text-xs sm:text-sm font-bold text-red-700 hover:text-red-900 flex items-center group-hover:underline"
        >
            Start <span className="hidden sm:inline ml-1">Journey</span> <TrendingUp className="w-4 h-4 ml-1" />
        </button>
    </div>
  </motion.div>
);

const TrainingPage: React.FC = () => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isRegisterOpen, setIsRegisterOpen] = useState(false);

  const scroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const scrollAmount = window.innerWidth < 640 ? 300 : 340;
      scrollContainerRef.current.scrollBy({ left: direction === 'left' ? -scrollAmount : scrollAmount, behavior: 'smooth' });
    }
  };

  return (
    <div className="bg-gray-50 text-gray-900 overflow-x-hidden py-8 sm:py-16">
      <RegistrationModal isOpen={isRegisterOpen} onClose={() => setIsRegisterOpen(false)} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-6 sm:mb-10 border-b border-gray-200 pb-6">
            <div className="w-full md:w-3/4 text-left">
              <span className="text-xs sm:text-sm font-bold text-red-600 uppercase tracking-widest mb-2 block">Career Guidance</span>
              <h2 className="text-2xl sm:text-4xl font-extrabold text-gray-900">
                Success <span className="text-red-600">Roadmaps</span>
              </h2>
              <p className="text-gray-500 mt-2 sm:mt-3 text-sm sm:text-base max-w-xl">
                Expert-designed paths to take you from beginner to high-paid professional.
              </p>
            </div>
            
            <div className="hidden sm:flex mt-6 md:mt-0 space-x-2">
                <button onClick={() => scroll('left')} aria-label="Scroll left to see more roadmaps" className="p-2 sm:p-3 bg-white rounded-full shadow-md border border-gray-100 hover:text-red-600 transition-all active:scale-95">
                    <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6" />
                </button>
                <button onClick={() => scroll('right')} aria-label="Scroll right to see more roadmaps" className="p-2 sm:p-3 bg-white rounded-full shadow-md border border-gray-100 hover:text-red-600 transition-all active:scale-95">
                    <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6" />
                </button>
            </div>
        </div>

        <div className="relative -mx-4 sm:mx-0">
            <motion.div
              ref={scrollContainerRef}
              className="flex overflow-x-auto space-x-2 sm:space-x-6 pb-6 pt-2 scrollbar-hide-custom snap-x snap-mandatory px-4 sm:px-0"
              initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.1 }}
            >
              {roadmaps.map((map) => (
                <div key={map.id} className="snap-center">
                    <RoadmapCard 
                        roadmap={map} 
                        onRegisterClick={() => setIsRegisterOpen(true)} 
                    />
                </div>
              ))}
            </motion.div>
        </div>
      </div>

      <style>
        {`
          .scrollbar-hide-custom::-webkit-scrollbar { display: none; }
          .scrollbar-hide-custom { -ms-overflow-style: none; scrollbar-width: none; }
        `}
      </style>
    </div>
  );
};

export default TrainingPage;